// See NotificationBar.spec.md for NB-FR-005

import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useDeferredValue, useMemo } from 'react'
import EventsErrorAlert, { EVENTS_EMPTY_MESSAGE } from 'core/audit-events/EventsErrorAlert'
import { eventColumns, toEventRows } from 'core/audit-events/eventColumns'
import { getEventRowContext } from 'core/audit-events/helpers'
import type { AuditEvent } from 'core/audit-events/models'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import VisuallyHidden from 'core/elements/VisuallyHidden'
import type Theme from 'core/themes/model'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { matchesSearch, newerThan, sortUnreadFirst } from './helpers'

// The toggle opens the drawer at a height that shows this many rows (NB-FR-004)
const DEFAULT_VISIBLE_ROWS = 5
export const ROW_HEIGHT = 48
export const HEADER_HEIGHT = 36
export const DEFAULT_DRAWER_HEIGHT = HEADER_HEIGHT + DEFAULT_VISIBLE_ROWS * ROW_HEIGHT
// The bar's controls row: 36 px controls with 8 px padding above and below
export const CONTROLS_HEIGHT = 52
// NB-FR-001: The space DefaultFrame keeps for the collapsed bar (controls plus the 1 px top border).
// An open drawer grows upward over the page and the sidebar instead of taking more space.
export const COLLAPSED_BAR_HEIGHT = CONTROLS_HEIGHT + 1

const LOADING_MESSAGE = 'Loading events...'
const NO_MATCHES_MESSAGE = 'No events match your search.'
// NB-FR-005: The drawer shows the top events only; the page has the rest
const MAX_DRAWER_EVENTS = 25

const visibleColumns = eventColumns.filter(({ display }) => display !== false)

interface Props {
  id: string
  events: AuditEvent[]
  // Rows newer than this were unread when the drawer opened, and stay marked as new
  highlightCutoff: string | null
  height: number
  // Filters the rows as the user types (NB-FR-008)
  search: string
  // Where "Open Notification Center" in the footer goes
  notificationCenterPath: string
  loading: boolean
  error: boolean
  onRetry: () => void
  // The app's current region; only its resources link to their details pages (NB-FR-005)
  activeRegion?: string | null
  // The app's current tenant; snapshots link only within it (NB-FR-005)
  activeTenantId?: string | null
}

export default function NotificationBarDrawer({
  id,
  events,
  highlightCutoff,
  height,
  search,
  notificationCenterPath,
  loading,
  error,
  onRetry,
  activeRegion,
  activeTenantId,
}: Props) {
  const classes = useStyles({ height })
  // NB-FR-005: Names and Object links use all the loaded events, as on the page (NCP-FR-010)
  const rowContext = useMemo(
    () => getEventRowContext(events, activeRegion, activeTenantId),
    [events, activeRegion, activeTenantId],
  )
  // NB-FR-005: Sorted (unread first) and formatted once per load or opening, not per keystroke.
  // Rows keep their event's stable ID, so polls don't remount every row.
  const sortedRows = useMemo(() => {
    const isNew = newerThan(highlightCutoff)
    return toEventRows(sortUnreadFirst(events, highlightCutoff), rowContext).map((row) => ({
      row,
      isNew: isNew(row),
    }))
  }, [events, highlightCutoff, rowContext])
  // NB-FR-008: Typing stays responsive; the rows follow as soon as React has time
  const deferredSearch = useDeferredValue(search)
  const matchingRows = useMemo(
    () => sortedRows.filter(({ row }) => matchesSearch(row, deferredSearch)),
    [sortedRows, deferredSearch],
  )
  // NB-FR-005: Only the top 25 (unread first), after the search
  const rows = matchingRows.slice(0, MAX_DRAWER_EVENTS)
  const hasMore = matchingRows.length > MAX_DRAWER_EVENTS

  // While loading, the table's loader says so; the empty and no-match texts wait for the result
  let message: string | null = null
  if (!loading && !events.length && !error) message = EVENTS_EMPTY_MESSAGE
  else if (!loading && events.length && !rows.length) message = NO_MATCHES_MESSAGE

  return (
    <div id={id} className={classes.drawer}>
      {error && <EventsErrorAlert onRetry={onRetry} className={classes.notice} />}
      {/* NB-FR-005: The same loader as the app's tables (core/elements/grid), over the rows */}
      <Progress overlay loading={loading} message={LOADING_MESSAGE} className={classes.content}>
        <table className={classes.table} aria-label="Recent events">
          <thead>
            <tr>
              {visibleColumns.map(({ key, label }) => (
                <th key={String(key)} scope="col" className={classes.headCell}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, isNew }) => (
              <tr
                key={row.id}
                className={clsx(classes.row, isNew && classes.newRow)}
                data-new={isNew || undefined}
              >
                {visibleColumns.map(({ key, CellComponent }, columnIndex) => (
                  <td key={String(key)} className={classes.cell}>
                    {columnIndex === 0 && isNew && <VisuallyHidden>New: </VisuallyHidden>}
                    {CellComponent ? (
                      <CellComponent value={row[key]} item={row} />
                    ) : (
                      // Event, Object Type, Object and Initiator are already formatted strings
                      <Text variant="body2" component="span">
                        {String(row[key])}
                      </Text>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {message && (
          <Text variant="body2" className={classes.message}>
            {message}
          </Text>
        )}
      </Progress>
      {hasMore && (
        <div className={classes.footer}>
          <Text variant="body2" component="span">
            Showing the latest <strong>{MAX_DRAWER_EVENTS}</strong> events.
          </Text>
          <SimpleLink src={notificationCenterPath}>Open Notification Center to see all</SimpleLink>
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme, { height: number }>((theme) => ({
  drawer: {
    height: ({ height }) => height,
    overflowY: 'auto',
    backgroundColor: theme.components.table.background,
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  // Lets the loader overlay cover the drawer even when the table only has its header
  content: {
    minHeight: '100%',
  },
  notice: {
    padding: theme.spacing(1, 2),
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    // The bar spans the full window, so the outer columns get extra room from its edges
    '& th:first-child, & td:first-child': {
      paddingLeft: theme.spacing(4),
    },
    '& th:last-child, & td:last-child': {
      paddingRight: theme.spacing(4),
    },
  },
  // Same typography and background as the app's grid header (core/elements/grid)
  headCell: {
    ...theme.typography.caption2,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    height: HEADER_HEIGHT,
    padding: theme.spacing(0, 2),
    textAlign: 'left',
    color: theme.components.table.headColor,
    backgroundColor: theme.components.table.hoverBackground,
    boxShadow: `inset 0 -1px 0 ${theme.components.table.border}`,
  },
  row: {
    height: ROW_HEIGHT,
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  // NB-FR-005: unread rows are marked by more than color (screen-reader "New:" prefix too)
  newRow: {
    backgroundColor: hexToRgbaCss(theme.components.tab.activeBackground, 0.06),
    '& td:first-child': {
      boxShadow: `inset 3px 0 0 ${theme.components.tab.activeText}`,
      fontWeight: 600,
    },
  },
  cell: {
    padding: theme.spacing(0, 2),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 320,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.components.table.hoverBackground,
    color: theme.components.typography.passive,
  },
  message: {
    padding: theme.spacing(2),
    color: theme.components.typography.passive,
  },
}))
